import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ExternalLink, FileText, Loader2, User } from 'lucide-react'
import { trpc } from '@/trpcReact'
import { LicenseAgreementDialog } from '@/components/LicenseAgreementDialog'
import { DBTables } from '../../types/supabaseSugarTypes'

type Subscription = DBTables['subscriptions']['Row']

interface CustomerInfoCardProps {
  customerId?: string
  email: string
  subscription?: Subscription
}

export function CustomerInfoCard({ customerId, email, subscription }: CustomerInfoCardProps) {
  const [licenseDialogOpen, setLicenseDialogOpen] = React.useState(false)
  const {
    data: portalUrl,
    isError: isCustomerPortalError,
    isLoading: isLoadingPortalUrl,
  } = trpc.getCustomerPortalUrl.useQuery({}, { enabled: !!customerId })
  const customerPortalLink = portalUrl?.customerPortalLink

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <User className="h-5 w-5" />
          Account
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Email</span>
          <span className="text-sm font-medium">{email}</span>
        </div>
        {subscription && (
          <>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                License agreement
                {subscription.license_agreement_date && (
                  <span className="text-xs">
                    {' — agreed '}
                    {new Date(subscription.license_agreement_date).toLocaleDateString(undefined, {
                      dateStyle: 'medium',
                    })}
                  </span>
                )}
              </span>
              <Button variant="outline" size="sm" onClick={() => setLicenseDialogOpen(true)}>
                <FileText className="h-3 w-3 mr-1" />
                Review
              </Button>
            </div>
            <LicenseAgreementDialog
              subscription={subscription}
              open={licenseDialogOpen}
              onOpenChange={setLicenseDialogOpen}
            />
          </>
        )}
        {customerId && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              Subscription and payment management
            </span>
            {isLoadingPortalUrl ? (
              <Button variant="outline" size="sm" disabled>
                <Loader2 className="h-3 w-3 animate-spin" />
                Loading...
              </Button>
            ) : customerPortalLink ? (
              <a
                href={customerPortalLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium underline hover:no-underline inline-flex items-center gap-1"
              >
                Manage
                <ExternalLink className="h-3 w-3" />
              </a>
            ) : (
              <Button variant="outline" size="sm" disabled>
                {isCustomerPortalError ? 'Unavailable' : 'Manage'}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
